// Doc 13 §8 — the only guard on a sync. There is no undo: a sync's inverse isn't
// reconstructible from a ViewModel snapshot (it deletes template rows and rebases
// provenance across two lists), so this dialog states the exact counts before the write.
// Modelled on TemplateDeleteDialog (Doc 07 §5).

import type { ReactNode } from "react";
import { Button, Dialog, vars } from "@pack-it-up/ui-web";
import type { SyncCounts } from "@pack-it-up/core";
import { syncChangeCount } from "./SyncModeBar.js";

export interface SyncConfirmDialogProps {
  templateName: string;
  counts: SyncCounts;
  applying: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const lines = (counts: SyncCounts): string[] => {
  const out: string[] = [];
  if (counts.updates > 0) out.push(`${counts.updates} entr${counts.updates === 1 ? "y" : "ies"} updated`);
  if (counts.adds > 0) out.push(`${counts.adds} entr${counts.adds === 1 ? "y" : "ies"} added`);
  if (counts.removes > 0) out.push(`${counts.removes} entr${counts.removes === 1 ? "y" : "ies"} removed`);
  if (counts.conflicts > 0)
    out.push(`${counts.conflicts} conflict${counts.conflicts === 1 ? "" : "s"} flagged (only the ticked ones overwrite)`);
  return out;
};

export const SyncConfirmDialog = ({
  templateName,
  counts,
  applying,
  onConfirm,
  onClose,
}: SyncConfirmDialogProps): ReactNode => (
  <Dialog
    open
    onClose={onClose}
    title={`Sync to “${templateName}”?`}
    subtitle="The template's contents become exactly the ticked entries."
    compact
    footer={
      <span style={{ display: "flex", justifyContent: "flex-end", gap: vars.space.sm }}>
        <Button variant="ghost" size="sm" onClick={onClose} disabled={applying}>
          Cancel
        </Button>
        <Button size="sm" onClick={onConfirm} disabled={applying || syncChangeCount(counts) === 0}>
          {applying ? "Syncing…" : "Sync changes"}
        </Button>
      </span>
    }
  >
    <ul style={{ margin: 0, paddingLeft: vars.space.lg, fontSize: vars.font.sizeSm, color: vars.color.text }}>
      {lines(counts).map((l) => (
        <li key={l}>{l}</li>
      ))}
    </ul>
    <p style={{ marginTop: vars.space.md, marginBottom: 0, fontSize: vars.font.sizeXs, color: vars.color.textMuted }}>
      This can&apos;t be undone.
    </p>
  </Dialog>
);
